import { compose } from 'recompose';
import { connect } from 'react-redux';
import { showToast } from 'modules/layout/layout.actions';
import Spinner from 'modules/common/spinner.component';
import { Box, Card, CardContent, Checkbox, Tooltip, Typography } from '@mui/material';
import CrudTable from 'modules/clients/details/crudTable.component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import React, { useState } from 'react';
import {
  DELETE_VENDOR_BANKING_INFO_FAILURE,
  POST_VENDOR_BANKING_INFO_FAILURE,
  UPDATE_VENDOR_BANKING_INFO_FAILURE
} from 'modules/authorizations/store/types/dataManagementTypes';
import {
  updateVendorBankingInfo,
  postVendorBankingInfo,
  deleteVendorBankingInfo,
  getVendorBanksByVendorId
} from 'modules/authorizations/store/actions/dataManagement.actions';
import { flattenBanks } from './utils';
import { sortByTitle, sortCountries, USA_COUNTRY_ID } from 'modules/common/utils';

const VendorBankingInfoCard = props => {
  const [isLoading, setIsLoading] = useState(false);

  const { vendorBanks, vendorId, isReadOnly, enumerations } = props;

  const accountTypes =
    enumerations && enumerations.bankAccountTypes && enumerations.bankAccountTypes.length > 0
      ? enumerations.bankAccountTypes.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];

  const accountLocationTypes =
    enumerations && enumerations.bankAccountLocationTypes && enumerations.bankAccountLocationTypes.length > 0
      ? enumerations.bankAccountLocationTypes.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];      

  const statusTypes =
    enumerations && enumerations.bankAccountStatuses && enumerations.bankAccountStatuses.length > 0
      ? enumerations.bankAccountStatuses.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];

  const currencyOptions =
    enumerations && enumerations.currencies && enumerations.currencies.length > 0
      ? enumerations.currencies.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];

  const provinces =
    enumerations && enumerations.provinces && enumerations.provinces.length > 0
      ? sortByTitle(enumerations.provinces.filter(s => s.isActive === true && s.title))
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];

  const countries =
    enumerations && enumerations.countries && enumerations.countries.length > 0
      ? sortCountries(enumerations.countries.filter(s => s.isActive === true && s.title))
      : [{ id: USA_COUNTRY_ID, title: 'United States' }];

  const bankingSummaries = [
    {
      header: 'Status',
      accessorKey: 'statusId',
      type: 'dropdown-v2',
      options: statusTypes,
      required: true,
      tab: 1,
      size: 35,
      enableColumnDragging: false,
      enableColumnActions: false,
      enableResizing: false,
      hideInTable: true
    },
    {
      header: 'Account Type',
      accessorKey: 'accountTypeId',
      type: 'dropdown-v2',
      options: accountTypes,
      required: true,
      tab: 1,
      size: 35,
      enableColumnDragging: false,
      enableColumnActions: false,
      enableResizing: false,
      hideInTable: true
    },
    {
      header: 'Account Location',
      accessorKey: 'accountLocationId',
      type: 'dropdown-v2',
      required: true,
      options: accountLocationTypes,
      tab: 1,
      size: 100,
      enableColumnDragging: false,
      enableColumnActions: false,
      enableResizing: false,
      hideInTable: true
    },
    {
      header: 'Bank Name',
      accessorKey: 'bankName',
      type: 'text',
      required: false,
      tab: 1,
      size: 100,
      enableColumnDragging: false,
      enableColumnActions: false,
      enableResizing: false
    },
    {
      header: 'City',
      accessorKey: 'bankCity',
      type: 'text',
      required: false,
      tab: 1,
      hideInTable: true
    },
    {
      header: 'Postal',
      accessorKey: 'bankPostal',
      type: 'text',
      required: false,
      tab: 1,
      hideInTable: true
    },
    {
      header: 'Country',
      accessorKey: 'bankCountryId',
      type: 'dropdown-v2',
      options: countries,
      required: true,
      tab: 1,
      hideInTable: true
    },
    {
      header: 'State',
      accessorKey: 'bankProvinceId',
      type: 'dropdown-v2',
      options: provinces,
      parentField: 'bankCountryId',
      parentOptionKey: 'countryId',
      // Countries without provinces can't satisfy the requirement, so the field is hidden for them.
      shouldHideField: values => !!values.bankCountryId && !provinces.some(p => p.countryId === values.bankCountryId),
      required: true,
      tab: 1,
      hideInTable: true
    },
    {
      header: 'Account Holder Name',
      accessorKey: 'accountHolderName',
      type: 'text',
      required: false,
      tab: 1,
      hideInTable: true
    },
    {
      header: 'Phone Number',
      accessorKey: 'bankPhone',
      type: 'text',
      required: false,
      tab: 1,
      hideInTable: true
    },
    {
      header: 'Email',
      accessorKey: 'bankEmail',
      type: 'text',
      required: false,
      tab: 1,
      hideInTable: true
    },
    {
      header: 'Fax',
      accessorKey: 'bankFax',
      type: 'text',
      required: false,
      tab: 1,
      hideInTable: true
    },
    {
      header: 'Bank Website',
      accessorKey: 'bankWebsite',
      type: 'text',
      required: false,
      tab: 1,
      hideInTable: true
    },
    {
      header: 'Bank Contact Name',
      accessorKey: 'bankContactName',
      type: 'text',
      required: false,
      tab: 1,
      hideInTable: true
    },
    {
      header: 'Routing Number',
      accessorKey: 'routingNumber',
      type: 'text',
      required: false,
      tab: 1,
      hideInTable: true
    },
    {
      header: 'Account Number',
      accessorKey: 'accountNumber',
      type: 'text',
      required: false,
      tab: 1,
      hideInTable: true
    },
    {
      header: 'Currency Code',
      accessorKey: 'accountCurrencyId',
      type: 'dropdown-v2',
      options: currencyOptions,
      required: true,
      tab: 1,
      hideInTable: true
    },
    {
      header: 'Payment Code',
      accessorKey: 'paymentCode',
      type: 'text',
      required: false,
      tab: 1,
      hideInTable: true
    },
    {
      header: 'Transit Code',
      accessorKey: 'transitCode',
      type: 'text',
      required: false,
      tab: 1,
      hideInTable: true
    },
    {
      header: 'IBAN',
      accessorKey: 'ibanNumber',
      type: 'text',
      required: false,
      tab: 1,
      hideInTable: true
    },
    {
      header: 'Swift Code',
      accessorKey: 'bankSwiftCode',
      type: 'text',
      required: false,
      tab: 1,
      hideInTable: true
    },
    {
      header: 'Cuit Code',
      accessorKey: 'cuitCode',
      type: 'text',
      required: false,
      tab: 1,
      hideInTable: true
    },
    {
      header: 'Clabe',
      accessorKey: 'clabe',
      type: 'text',
      required: false,
      tab: 1,
      hideInTable: true
    },
    {
      header: 'Institution Number',
      accessorKey: 'institutionNumber',
      type: 'text',
      required: false,
      tab: 1,
      hideInTable: true
    },
    {
      header: 'Default Account',
      accessorKey: 'isDefaultAccount',
      type: 'checkbox',
      required: false,
      tab: 1,
      Cell: ({ cell }) => <Checkbox name="defaultAccount" label="Active" checked={cell.row.original.isDefaultAccount} disabled={false} />,
    },
    {
      header: 'Used For Wire',
      accessorKey: 'isPreferredForWire',
      type: 'checkbox',
      required: false,
      tab: 1,
      Cell: ({ cell }) => <Checkbox name="usedForWire" label="Active" checked={cell.row.original.isPreferredForWire} disabled={false} />,
      hideInTable: true
    },
    {
      header: 'Wire Instruction',
      accessorKey: 'wireInstruction',
      type: 'text',
      required: false,
      tab: 1,
      hideInTable: true
    },
    {
      header: 'Used For Ach',
      accessorKey: 'isPreferredForACH',
      type: 'checkbox',
      required: false,
      tab: 1,
      Cell: ({ cell }) => <Checkbox name="usedForAch" label="Active" checked={cell.row.original.isPreferredForACH} disabled={false} />,
      hideInTable: true
    },
    {
      header: 'Intermediary Required',
      accessorKey: 'hasIntermedReqd',
      type: 'checkbox',
      required: false,
      tab: 1,
      Cell: ({ cell }) => (
        <Checkbox name="intermediaryRequired" label="Active" checked={cell.row.original.hasIntermedReqd} disabled={false} />
      ),
      hideInTable: true
    },
    {
      header: 'Intermediary Banking Id',
      accessorKey: 'intermedBankingId',
      type: 'number',
      required: false,
      tab: 1,
      hideInTable: true
    },
    {
      header: 'Special Instructions',
      accessorKey: 'specialInstructions',
      type: 'text',
      required: false,
      tab: 1,
      hideInTable: true
    }
  ];

  // Backend columns are NOT NULL, so text fields must be sent as strings and ints as numbers.
  const buildBankPayload = (values, existing = { vendorBank: {}, bank: {} }) => ({
    vendorBank: {
      ...existing.vendorBank,
      vendorId: vendorId,
      paymentCode: values.paymentCode ?? '',
      transitCode: values.transitCode ?? '',
      cuitCode: values.cuitCode ?? '',
      institutionNumber: values.institutionNumber ?? ''
    },
    bank: {
      ...existing.bank,
      statusId: values.statusId,
      accountLocationId: values.accountLocationId,
      accountTypeId: values.accountTypeId,
      bankName: values.bankName ?? '',
      bankCity: values.bankCity ?? '',
      bankPostal: values.bankPostal ?? '',
      bankProvinceId: values.bankProvinceId || '00000000-0000-0000-0000-000000000000',
      bankCountryId: values.bankCountryId,
      accountHolderName: values.accountHolderName ?? '',
      accountHolderAddressId: 1, // TODO: Determine which address we actually want to use for this (primary vendor contact?)
      bankPhone: values.bankPhone ?? '',
      bankFax: values.bankFax ?? '',
      bankEmail: values.bankEmail ?? '',
      bankWebsite: values.bankWebsite ?? '',
      bankContactName: values.bankContactName ?? '',
      bankSwiftCode: values.bankSwiftCode ?? '',
      routingNumber: values.routingNumber ?? '',
      accountNumber: values.accountNumber ?? '',
      accountCurrencyId: values.accountCurrencyId,
      ibanNumber: values.ibanNumber ?? '',
      clabe: values.clabe ?? '',
      isDefaultAccount: values.isDefaultAccount === true,
      isPreferredForWire: values.isPreferredForWire === true,
      wireInstruction: values.wireInstruction ?? '',
      isPreferredForACH: values.isPreferredForACH === true,
      hasIntermedReqd: values.hasIntermedReqd === true,
      intermedBankingId: parseInt(values.intermedBankingId, 10) || 0,
      specialInstructions: values.specialInstructions ?? ''
    }
  });

  const updateBank = async (record, values) => {
    const existing = vendorBanks.find(b => b.vendorBank.vendorBankId === record.original.vendorBankId);
    const tempVal = buildBankPayload(values, existing);

    setIsLoading(true);
    const resp = await props.updateVendorBankingInfo(tempVal);
    setIsLoading(false);
    return resp.type !== UPDATE_VENDOR_BANKING_INFO_FAILURE;
  };

  const createBank = async values => {
    const tempVal = buildBankPayload(values);
    tempVal.vendorBank.bankId = 0;

    setIsLoading(true);
    const resp = await props.postVendorBankingInfo(tempVal);
    if (resp.type === POST_VENDOR_BANKING_INFO_FAILURE) {
      setIsLoading(false);
      return false;
    } else {
      setIsLoading(false);
      return true;
    }
  };

  const deleteBank = async record => {
    setIsLoading(true);
    const resp = await props.deleteVendorBankingInfo(record.original.vendorBankId);
    if (resp.type === DELETE_VENDOR_BANKING_INFO_FAILURE) {
      setIsLoading(false);
      return false;
    } else {
        setIsLoading(false);
        return true;
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          <Tooltip title={'File Contract'} key={'vendorBanks'} placement="right">
            <FontAwesomeIcon icon={['fas', 'file-contract']} size="2x" />
          </Tooltip>
          <Typography variant="h5" sx={{ paddingLeft: '20px', fontSize: '1.3125rem', fontWeight: 'bold' }}>
            Banking
          </Typography>
        </Box>
        {!isLoading && (
          <CrudTable
            editingMode={'modal'} // Can be changed to 'row' to edit inline
            columns={bankingSummaries}
            rows={flattenBanks(vendorBanks)}
            updateRow={updateBank}
            createRow={createBank}
            deleteRow={deleteBank}
            customAddText={'Add Bank'}
            hideEdit={isReadOnly}
          />
        )}
        {isLoading && <Spinner />}
      </CardContent>
    </Card>
  );
};

export default compose(
  connect(null, {
    updateVendorBankingInfo,
    postVendorBankingInfo,
    deleteVendorBankingInfo,
    getVendorBanksByVendorId,
    showToast
  })
)(VendorBankingInfoCard);
